import React from 'react';
import { Box } from '@mui/material';
import {
  RestaurantOutlined,
  ShoppingCartOutlined,
  DirectionsCarOutlined,
  LocalGasStationOutlined,
  ShoppingBagOutlined,
  ReceiptLongOutlined,
  BoltOutlined,
  HomeOutlined,
  LocalHospitalOutlined,
  MedicationOutlined,
  SchoolOutlined,
  MovieOutlined,
  FlightOutlined,
  SmartphoneOutlined,
  SubscriptionsOutlined,
  HealthAndSafetyOutlined,
  RequestQuoteOutlined,
  CardGiftcardOutlined,
  SpaOutlined,
  FitnessCenterOutlined,
  PetsOutlined,
  ChildCareOutlined,
  WorkOutlineOutlined,
  StorefrontOutlined,
  TrendingUpOutlined,
  SavingsOutlined,
  RedeemOutlined,
  ApartmentOutlined,
  ReplayOutlined,
  CategoryOutlined,
  LaptopOutlined,
  EmojiEventsOutlined,
  PieChartOutlineOutlined,
  ElderlyOutlined,
  ArrowUpward,
  ArrowDownward,
} from '@mui/icons-material';

// Common category icons. The key is what's stored in categories.icon.
// `types` says which category type(s) can pick the icon.
// Add new icons at the end of their group; never rename a key (existing categories refer to it).
const EXPENSE = ['expense'];
const INCOME = ['income'];
const BOTH = ['expense', 'income'];

export const CATEGORY_ICONS = [
  // Expense
  { key: 'food', label: 'Food & Dining', Icon: RestaurantOutlined, types: EXPENSE },
  { key: 'groceries', label: 'Groceries', Icon: ShoppingCartOutlined, types: EXPENSE },
  { key: 'transport', label: 'Transport', Icon: DirectionsCarOutlined, types: EXPENSE },
  { key: 'fuel', label: 'Fuel', Icon: LocalGasStationOutlined, types: EXPENSE },
  { key: 'shopping', label: 'Shopping', Icon: ShoppingBagOutlined, types: EXPENSE },
  { key: 'bills', label: 'Bills', Icon: ReceiptLongOutlined, types: EXPENSE },
  { key: 'electricity', label: 'Electricity', Icon: BoltOutlined, types: EXPENSE },
  { key: 'home', label: 'Rent & Home', Icon: HomeOutlined, types: EXPENSE },
  { key: 'health', label: 'Health', Icon: LocalHospitalOutlined, types: EXPENSE },
  { key: 'medicine', label: 'Medicine', Icon: MedicationOutlined, types: EXPENSE },
  { key: 'education', label: 'Education', Icon: SchoolOutlined, types: EXPENSE },
  { key: 'entertainment', label: 'Entertainment', Icon: MovieOutlined, types: EXPENSE },
  { key: 'travel', label: 'Travel', Icon: FlightOutlined, types: EXPENSE },
  { key: 'phone', label: 'Phone & Internet', Icon: SmartphoneOutlined, types: EXPENSE },
  { key: 'subscriptions', label: 'Subscriptions', Icon: SubscriptionsOutlined, types: EXPENSE },
  { key: 'insurance', label: 'Insurance', Icon: HealthAndSafetyOutlined, types: EXPENSE },
  { key: 'emi', label: 'EMI & Loans', Icon: RequestQuoteOutlined, types: EXPENSE },
  { key: 'personal-care', label: 'Personal Care', Icon: SpaOutlined, types: EXPENSE },
  { key: 'fitness', label: 'Fitness', Icon: FitnessCenterOutlined, types: EXPENSE },
  { key: 'pets', label: 'Pets', Icon: PetsOutlined, types: EXPENSE },
  { key: 'kids', label: 'Kids', Icon: ChildCareOutlined, types: EXPENSE },
  // Income
  { key: 'salary', label: 'Salary', Icon: WorkOutlineOutlined, types: INCOME },
  { key: 'business', label: 'Business', Icon: StorefrontOutlined, types: INCOME },
  { key: 'freelance', label: 'Freelance', Icon: LaptopOutlined, types: INCOME },
  { key: 'bonus', label: 'Bonus', Icon: EmojiEventsOutlined, types: INCOME },
  { key: 'investments', label: 'Investments', Icon: TrendingUpOutlined, types: INCOME },
  { key: 'dividends', label: 'Dividends', Icon: PieChartOutlineOutlined, types: INCOME },
  { key: 'savings', label: 'Interest & Savings', Icon: SavingsOutlined, types: INCOME },
  { key: 'rental', label: 'Rental Income', Icon: ApartmentOutlined, types: INCOME },
  { key: 'cashback', label: 'Cashback & Rewards', Icon: RedeemOutlined, types: INCOME },
  { key: 'refund', label: 'Refund', Icon: ReplayOutlined, types: INCOME },
  { key: 'pension', label: 'Pension', Icon: ElderlyOutlined, types: INCOME },
  // Both
  { key: 'gifts', label: 'Gifts', Icon: CardGiftcardOutlined, types: BOTH },
  { key: 'other', label: 'Other', Icon: CategoryOutlined, types: BOTH },
];

// Icons a category of the given type ('expense' | 'income') can use.
export const getCategoryIconsForType = (type) =>
  CATEGORY_ICONS.filter((icon) => icon.types.includes(type));

const ICONS_BY_KEY = Object.fromEntries(CATEGORY_ICONS.map((icon) => [icon.key, icon]));

// Icon component for a category: its chosen icon, or an up/down arrow for
// income/expense when none is chosen (the previous look).
export const getCategoryIcon = (category) =>
  ICONS_BY_KEY[category?.icon]?.Icon || (category?.type === 'income' ? ArrowUpward : ArrowDownward);

// Round badge in the category's colour with its icon in white.
export const CategoryIconBadge = ({ category, size = 28, sx }) => {
  const Icon = getCategoryIcon(category);
  return (
    <Box
      component="span"
      aria-hidden="true"
      sx={{
        width: size,
        height: size,
        borderRadius: '50%',
        flexShrink: 0,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: category?.color || 'grey.500',
        color: '#fff',
        '& svg': { fontSize: Math.round(size * 0.6) },
        ...sx,
      }}
    >
      <Icon />
    </Box>
  );
};
